import React, { useState, useEffect, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import ClickOutsideHolder from "../ClickOutsideHolder";
import ErrorBlock from "../ErrorBlock";
import { withTranslation } from "react-i18next";
import ReactSVG from "react-svg";
import Loader from "../Loader";
import useDebounce from "../../../../custom-hooks/useDebounce";

import "./styles.scss";

const ComboBoxMultipleCheckbox = props => {
    const {
        t,
        meta,
        input,
        placeholder,
        disabled,
        label,
        required,
        isLoading,
        showValuesCount,
        options: initOptions = [],
        disallow_select_all,
        withSearch,
        submitComboboxMulty,
        submitOnBlur,
        onOpen,
        isAsync,
        onInputChange,
        initialValues,
    } = props;

    const [showOptions, setShowOptions] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");

    const searchInputRef = useRef(null);

    const { debouncedValue: debouncedQuery, setDebouncedValue: setDebouncedQuery } = useDebounce(searchQuery, 300);

    const options = useMemo(() => {
        if (isAsync) {
            // merge together initialValues with current values and get unique options (without duplicates)
            const initKeys = initialValues && Array.isArray(initialValues) ? initialValues.map(item => item.value) : [];
            const inputKeys = input && input.value && Array.isArray(input.value) ? input.value.map(item => item.value) : [];
            const initOptionsKeys = initOptions && Array.isArray(initOptions) ? initOptions.map(option => option.value) : [];
            const uniqueKeys = [...new Set([...initKeys, ...inputKeys, ...initOptionsKeys])];
            const mergedOptions = [...(input.value || []), ...(initOptions || []), ...(initialValues || [])];
            const options = uniqueKeys.map(key => mergedOptions.find(option => option.value === key));
            return options;
            // return initOptions.length > 0 ? initOptions : input.value;
        }
        if (debouncedQuery) {
            const query = debouncedQuery.toLowerCase();
            // get selected options (to keep them once options are being filtered)
            const selectedOptions =
            input && input.value && Array.isArray(input.value)
                ? input.value.map(val => initOptions.find(option => option.value === val))
                : [];
            
            // make sure to not include selected options in filtered options to prevent duplicate options
            const filteredOptions = initOptions.filter(option => {
                const isMatchQuery = option.label.toLowerCase().includes(query);
                const isSelected =
                input && input.value && Array.isArray(input.value)
                    ? input.value.includes(option.value)
                    : false;
                return isMatchQuery && !isSelected;
            });
            return [...selectedOptions, ...filteredOptions];
        }
        return initOptions;
    }, [initOptions, debouncedQuery, isAsync]);

    useEffect(() => {
        if (isAsync) {
            onInputChange(debouncedQuery);
        }
    }, [isAsync, debouncedQuery]);

    const wrapperClassName = cx("input-element comboBox-multi-checkbox", {
        isOpen: showOptions,
        disabled,
    });

    const inputClassName = cx("input-element", {
        "input-element__error": meta && meta.touched && meta.error,
    });
    const buttonClassName = cx("input-field", {
        "input-field__error": meta && meta.touched && meta.error,
        'placeholder': placeholder
    });

    const showErrorBlock = meta && meta.touched && !meta.active && meta.error;

    const getLabel = value => {
        if (isAsync) {
            const option = options.find(option => option.value === value.value);
            return option && option.label.trim();
        } else {
            const option = options.find(option => option.value === value);
            return option && option.label.trim();
        }
    };

    const handleChange = (event, option) => {
        const value = isAsync ? option : option.value;
        const newValue = [...input.value];
        if (event.target.checked) {
            newValue.push(value);
        } else {
            newValue.splice(newValue.indexOf(value), 1);
        }
        input.onChange(newValue);
    };

    const clearAll = event => {
        // clear
        input.onChange([]);
    };

    const toggleSelectAll = event => {
        const optionsLength = options.length;
        const valuesLength = input.value.length;
        if (optionsLength === valuesLength) {
            // clear
            input.onChange([]);
        } else {
            // select
            // input.onChange(options.map(item => item.value));
            input.onChange(isAsync ? options : options.map(item => item.value));
            // if (isAsync) {
            //     input.onChange(options.map(item => item));

            // } else {
            //     input.onChange(options.map(item => item.value));
            // }
        }
    };

    const toggleOptions = () => {
        if (!disabled) {
            if (showOptions) {
                input.onBlur();
            } else {
                input.onFocus();
            }
            if (onOpen && typeof onOpen === "function") {
                onOpen();
            }
            setShowOptions(prev => !prev);
        }
    };

    const hideOptions = e => {
        const fieldKey = input && input.name;
        let isFieldControllerParent = false;
        let isFieldControllerSubmit = false; // is field controller submit button clicked

        if (fieldKey) {
            const path = e ? e.path || (e.composedPath && e.composedPath()) : [];
            for (let i = 0; i < path.length; i++) {
                if (path[i].getAttribute && path[i].getAttribute("data-field-key")) {
                    isFieldControllerParent = path[i].getAttribute("data-field-key") === fieldKey;
                    // break;
                }
                if (path[i].getAttribute && path[i].getAttribute("data-field-controller-submit")) {
                    isFieldControllerSubmit = path[i].getAttribute("data-field-controller-submit") === "true";
                    // break;
                }
            }
        }

        // make sure that not submit button was clicked, if submit button was clicked => options should be hidden
        if (isFieldControllerParent && !isFieldControllerSubmit) {
            return;
        }

        if (showOptions) {
            input.onBlur();
            setShowOptions(false);
        }
        const { value, name } = input;
        if (submitOnBlur && meta.touched) {
            submitComboboxMulty(value, name);
        }
    };

    const renderCheckboxGroup = () => {
        const optionsLength = options.length;
        const valuesLength = input.value.length;

        return (
            <div className="optionsWrapper comboBoxMultipleCheckbox">
                <div className="buttonsWrapper">
                    <button
                        type="button"
                        className={cx("btn btn-add button-clear-all", { disabled: valuesLength === 0 || disabled })}
                        onClick={clearAll}
                    >
                        <ReactSVG className="button-icon" src={`/data/svg/remove_circle.svg`} />
                        {t("clear all results")}
                    </button>
                    <div className={cx("checkbox checkbox-select-all", { disabled: optionsLength === 0 || disabled })}>
                        {!disallow_select_all && (
                            <label htmlFor="selectAll">
                                <input
                                    type="checkbox"
                                    name="selectAll"
                                    id="selectAll"
                                    checked={optionsLength !== 0 && optionsLength === valuesLength}
                                    onChange={toggleSelectAll}
                                />
                                <span className="checkbox-text">
                                    <ReactSVG
                                        className="checkbox__unchecked-icon"
                                        src={`/data/svg/checkbox_unchecked.svg`}
                                    />
                                    <ReactSVG
                                        className="checkbox__checked-icon"
                                        src={`/data/svg/checkbox_checked.svg`}
                                    />
                                    <span>{t("select all results")}</span>
                                </span>
                            </label>
                        )}
                    </div>
                    {withSearch && (
                        <div className="input-element">
                            <input
                                ref={searchInputRef}
                                name={`${input.name}-SEARCH`}
                                placeholder={t("Search")}
                                className="input-field"
                                type="text"
                                onChange={e => setSearchQuery(e.target.value)}
                            />
                        </div>
                    )}
                </div>
                <div className="options">
                    {options.map((option, index) => (
                        <div className={cx("checkbox", { disabled: option.disabled || disabled })} key={index}>
                            <label htmlFor={option.value}>
                                <input
                                    type="checkbox"
                                    name={`${input.label}[${index}]`}
                                    id={option.value}
                                    // checked={input.value.some(item => isAsync ? item.value === option.value : item === option.value)}
                                    checked={input && Array.isArray(input.value) && input.value.some(item => isAsync ? item.value === option.value : item === option.value)}
                                    // onChange={event => handleChange(event, option.value)}
                                    onChange={event => handleChange(event, option)}
                                    disabled={option.disabled || disabled}
                                />
                                <span title={option.label} className="checkbox-text">
                                    <ReactSVG
                                        className="checkbox__unchecked-icon"
                                        src={`/data/svg/checkbox_unchecked.svg`}
                                    />
                                    <ReactSVG
                                        className="checkbox__checked-icon"
                                        src={`/data/svg/checkbox_checked.svg`}
                                    />
                                    <span>{option.label}</span>
                                </span>
                            </label>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const renderSelectedValue = (item, index, arr) => {
        if (isAsync) {
            return (
                <span key={index} className="input-value">
                    {/* {getLabel(item)} */}
                    {item.label}
                    {arr.length > 1 && index !== arr.length - 1 && ", "}
                </span>
            );
        }
        if (options.find(i => item === i.value)) {
            return (
                <span key={index} className="input-value">
                    {getLabel(item)}
                    {arr.length > 1 && index !== arr.length - 1 && ", "}
                </span>
            );
        }
        return null;
    };

    const renderSelectedValues = values => {
        if (showValuesCount) {
            return (
                <div className="input-counted-values">
                    {values.length === 1 && <span>{getLabel(values[0])}</span>}
                    {values.length > 1 && (
                        <span>
                            ({values.length}) {t("select all results")}
                        </span>
                    )}
                </div>
            );
        }
        return values.map(renderSelectedValue);
    };

    useEffect(() => {
        if (showOptions) {
            const selectOptions = document.querySelector(".optionsWrapper");
            if (selectOptions.scrollIntoViewIfNeeded) {
                selectOptions.scrollIntoViewIfNeeded(false);
            } else {
                selectOptions.scrollIntoView(false);
            }
            if (searchInputRef && searchInputRef.current) {
                searchInputRef.current.focus();
            }
        }
    }, [showOptions]);

    return (
        <div className={inputClassName}>
            <div className="input-label">
                {label}
                {required && <span className="required-field">*</span>}
            </div>
            <ClickOutsideHolder className={wrapperClassName} onClickOutside={hideOptions}>
                <button className={buttonClassName} type="button" onClick={toggleOptions}>
                    {isLoading && (
                        <Loader
                            loaderContainerClass="container-loader container-loader-comboBox-multi-checkbox"
                            loaderClass="input-loader"
                        />
                    )}
                    {Array.isArray(input.value) && input.value.length > 0 ? renderSelectedValues(input.value) : placeholder}
                </button>
                {showOptions && renderCheckboxGroup()}
            </ClickOutsideHolder>
            {showErrorBlock && <ErrorBlock error={meta.error} className="m-r-10" />}
        </div>
    );
};

ComboBoxMultipleCheckbox.propTypes = {
    options: PropTypes.array.isRequired,
    input: PropTypes.object.isRequired,
    disabled: PropTypes.bool,
    placeholder: PropTypes.string,
    error: PropTypes.object,
    meta: PropTypes.shape({
        error: PropTypes.string,
        label: PropTypes.string,
        touched: PropTypes.bool,
    }).isRequired,
};

export default withTranslation()(ComboBoxMultipleCheckbox);
